import { config } from '../Environment';
import { RecordingData } from '../hooks/useMediaRecorder';
import { AutoPlayConfig } from '../components/recording/AutoPlaySettings';

const API_BASE_URL = config.API_BASE;

interface PresignedUrlResponse {
  uploadUrl: string;
  key: string;
  expiresIn: number;
  maxSize: number;
}

interface SaveRecordingRequest {
  key: string;
  type: 'audio' | 'video';
  activityId: string;
  duration: number;
  size: number;
  mimeType: string;
  autoPlay?: AutoPlayConfig;
}

interface SaveRecordingResponse {
  message: string;
  recording: RecordingData & { autoPlay?: AutoPlayConfig };
}

interface UpdateAutoPlayRequest {
  type: 'audio' | 'video';
  activityId: string;
  autoPlay: AutoPlayConfig;
}

interface UpdateAutoPlayResponse {
  message: string;
  autoPlay: AutoPlayConfig;
}

/**
 * Get presigned URL for uploading recording to R2 (for prescriptions)
 */
export async function getPresignedUploadUrlForPrescription(
  prescriptionId: string,
  type: 'audio' | 'video',
  activityId: string,
  retryFetch?: (url: string, options?: RequestInit) => Promise<Response>
): Promise<PresignedUrlResponse> {
  const url = `${API_BASE_URL}/api/prescriptions/${prescriptionId}/recordings/presigned-url`;
  const options: RequestInit = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ type, activityId }),
  };

  const response = retryFetch
    ? await retryFetch(url, options)
    : await fetch(url, options);

  if (!response.ok) {
    if (response.status === 429) {
      const error = await response.json();
      throw new Error(error.message || 'Rate limit exceeded. Please try again later.');
    }
    const error = await response.json().catch(() => ({ error: 'Failed to get presigned URL' }));
    throw new Error(error.error || 'Failed to get presigned URL');
  }

  return response.json();
}

/**
 * Upload recording blob directly to R2 using presigned URL
 */
export async function uploadRecordingToR2(
  uploadUrl: string,
  blob: Blob,
  mimeType: string
): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': mimeType,
    },
    body: blob,
  });

  if (!response.ok) {
    throw new Error(`Failed to upload to R2: ${response.statusText}`);
  }
}

/**
 * Save recording metadata to backend after successful upload (for prescriptions)
 */
export async function saveRecordingMetadataForPrescription(
  prescriptionId: string,
  data: SaveRecordingRequest,
  retryFetch?: (url: string, options?: RequestInit) => Promise<Response>
): Promise<SaveRecordingResponse> {
  const url = `${API_BASE_URL}/api/prescriptions/${prescriptionId}/recordings`;
  const options: RequestInit = {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(data),
  };

  const response = retryFetch
    ? await retryFetch(url, options)
    : await fetch(url, options);

  if (!response.ok) {
    if (response.status === 413) {
      const error = await response.json();
      throw new Error(error.error || 'File size exceeds limit');
    }
    const error = await response.json().catch(() => ({ error: 'Failed to save recording metadata' }));
    throw new Error(error.error || 'Failed to save recording metadata');
  }

  return response.json();
}

/**
 * Delete recording from R2 and remove metadata (for prescriptions)
 */
export async function deleteRecordingFromPrescription(
  prescriptionId: string,
  type: 'audio' | 'video',
  activityId: string,
  retryFetch?: (url: string, options?: RequestInit) => Promise<Response>
): Promise<void> {
  const url = `${API_BASE_URL}/api/prescriptions/${prescriptionId}/recordings`;
  const options: RequestInit = {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ type, activityId }),
  };

  const response = retryFetch
    ? await retryFetch(url, options)
    : await fetch(url, options);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to delete recording' }));
    throw new Error(error.error || 'Failed to delete recording');
  }
}

/**
 * Update auto-play settings for a prescription recording
 */
export async function updateAutoPlaySettingsForPrescription(
  prescriptionId: string,
  data: UpdateAutoPlayRequest,
  retryFetch?: (url: string, options?: RequestInit) => Promise<Response>
): Promise<UpdateAutoPlayResponse> {
  const url = `${API_BASE_URL}/api/prescriptions/${prescriptionId}/recordings/autoplay`;
  const options: RequestInit = {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(data),
  };

  const response = retryFetch
    ? await retryFetch(url, options)
    : await fetch(url, options);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to update auto-play settings' }));
    throw new Error(error.error || 'Failed to update auto-play settings');
  }

  return response.json();
}
